import { DeleteIcon } from "@chakra-ui/icons";
import {
  Button,
  Divider,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spacer,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { useData } from "../../Contexts/DataContext";
import { deleteFilesFromUrls, uploadFile } from "../../Utils/fileStorageUtils";
import RequirementQuestions from "./RequirementQuestions";
import ViewEditRequirement from "./ViewEditRequirement";
import ConfirmDialog from "../Shared/ConfirmDialog";
import TaskPanel from "./Tasks/TaskPanel";

const AddEditRequirementModal = ({
  isOpen,
  onClose,
  requirement = null,
  type = null,
  initialTabIndex = 0,
}) => {
  const toast = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    selectedSow,
    tasks,
    actions,
    addDocument,
    updateDocument,
    deleteDocument,
    selectedClient,
    developers,
  } = useData();
  const deliverables = selectedSow?.deliverables || [];
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  const [isFormValid, setIsFormValid] = useState(false);

  const [formData, setFormData] = useState({
    ...requirement,
    screenshots: requirement?.screenshots || [],
  });
  const [activeTab, setActiveTab] = useState(initialTabIndex);

  const requirementActions = requirement?.id
    ? actions?.filter((action) => action.parentId === requirement.id)
    : [];
  const requirementTasks = requirement?.id
    ? tasks?.filter((task) => task.requirementId === requirement.id)
    : [];


  // console.log("tasks", tasks);
  // console.log("req", requirement)

  useEffect(() => {
    if (type !== "new") {
      setIsFormValid(true);
      return;
    }

    const { title, deliverableId, assignedTo, dueDate, detail } = formData;

    const isValid =
      title?.trim() &&
      deliverableId?.trim() &&
      assignedTo?.trim() &&
      dueDate?.trim() &&
      detail?.trim();

    setIsFormValid(Boolean(isValid));
  }, [formData, type]);

  useEffect(() => {
    if (requirement) {
      setFormData({
        ...requirement,
        screenshots: requirement.screenshots || [],
      });
    } else {
      setFormData({ screenshots: [] });
    }
  }, [requirement]);

  useEffect(() => {
    if (requirement) {
      setFormData({
        ...requirement,
        screenshots: requirement.screenshots || [],
      });
    } else {
      setFormData({
        status: "Not Started",
        screenshots: [],
      });
    }
  }, [requirement, type]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setFormData((prev) => ({
      ...prev,
      screenshots: [...(prev.screenshots || []), ...files],
    }));
  };

  const handleFileRemove = (index) => {
    setFormData((prev) => ({
      ...prev,
      screenshots: prev.screenshots.filter((_, i) => i !== index),
    }));
  };

  const getCleanedRequirementData = (data) => {
    const {
      title,
      status,
      deliverableId,
      assignedTo,
      dueDate,
      detail,
      screenshots = [],
      linked_email = "",
    } = data;

    return {
      title,
      deliverableId,
      assignedTo,
      dueDate,
      detail,
      screenshots,
      linked_email,
      status: type === "new" ? "Not Started" : status,
    };
  };

  const handleUpdateRequirement = async () => {
    setIsSubmitting(true);
    try {
      let uploadedScreenshots = [];

      if (formData.screenshots?.length > 0) {
        uploadedScreenshots = await Promise.all(
          formData.screenshots.map(async (file) => {
            if (file instanceof File) {
              return await uploadFile(
                file,
                `screenshots/requirements/${formData.id || "temp"}`
              );
            }
            return file;
          })
        );
      }

      const cleanedData = getCleanedRequirementData({
        ...formData,
        screenshots: uploadedScreenshots,
      });

      if (type === "new") {
        await addDocument("requirements", {
          ...cleanedData,
          clientId: selectedClient?.id,
        });

        toast({
          title: "Requirement added successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      } else {
        await updateDocument("requirements", formData.id, cleanedData);
        toast({
          title: "Requirement updated successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
      }

      onClose();
    } catch (error) {
      console.error(
        `Error ${type === "new" ? "adding" : "updating"} requirement:`,
        error
      );
      toast({
        title: `Error ${type === "new" ? "adding" : "updating"} requirement`,
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteRequirement = async () => {
    if (!formData.id) {
      toast({
        title: "No requirement to delete",
        status: "warning",
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      // 🔥 Delete screenshots from Firebase Storage
      if (formData.screenshots?.length > 0) {
        await deleteFilesFromUrls(formData.screenshots);
      }

      // 🗑️ Delete requirement from Firestore
      await deleteDocument("requirements", formData.id);

      toast({
        title: "Requirement deleted successfully",
        status: "success",
        duration: 3000,
        isClosable: true,
      });

      onClose();
    } catch (error) {
      console.error("Error deleting requirement:", error);
      toast({
        title: "Error deleting requirement",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalOverlay />
      <ModalContent maxW="container.lg">
        <ModalHeader
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          bg="gray.700"
          color="white"
        >
          {formData.title || "New Requirement / Request"}
          <ModalCloseButton color="white" />
        </ModalHeader>

        <ModalBody pb={4} bg={"gray.50"} p={0}>
          <Tabs
            index={activeTab}
            variant="enclosed"
            onChange={(index) => setActiveTab(index)}
            isLazy
          >
            <TabList>
              <Tab>Requirement Details</Tab>

              {type !== "new" && (
                <>
                  <Tab>Tasks ({requirementTasks?.length || 0})</Tab>
                  {/* <Tab>Queries ({requirementActions?.length || 0})</Tab> */}
                </>
              )}
            </TabList>

            <TabPanels h="55vh" overflowY="auto">
              <TabPanel>
                <ViewEditRequirement
                  developers={developers}
                  deliverables={deliverables}
                  formData={formData}
                  onChange={handleInputChange}
                  onFileChange={handleFileChange}
                  onFileRemove={handleFileRemove}
                  type={type}
                />
              </TabPanel>

              <TabPanel>
                {type !== "new" && <TaskPanel requirementId={requirement.id} />}
              </TabPanel>

              {/* <TabPanel>
                {type !== "new" && requirement && (
                  <RequirementQuestions
                    actions={actions}
                    addItem={addDocument}
                    requirementId={requirement.reqId}
                  />
                )}
              </TabPanel> */}
            </TabPanels>
          </Tabs>
        </ModalBody>

        <Divider />

        <ModalFooter w="full">
          {activeTab === 0 ? (
            <>
              {type !== "new" && requirement && (
                <Button
                  leftIcon={<DeleteIcon />}
                  colorScheme="red"
                  variant="outline"
                  fontSize="xs"
                  onClick={() => setShowDeleteDialog(true)}
                >
                  Delete
                </Button>
              )}
              <Spacer />
              <HStack>
                <Button
                  fontSize="xs"
                  mr={3}
                  colorScheme="green"
                  onClick={handleUpdateRequirement}
                  isDisabled={!isFormValid || isSubmitting}
                  isLoading={isSubmitting}
                  loadingText={type === "new" ? "Adding" : "Saving"}
                >
                  Confirm
                </Button>

                <Button
                  fontSize="xs"
                  variant="outline"
                  colorScheme="gray"
                  onClick={onClose}
                >
                  Cancel
                </Button>
              </HStack>
            </>
          ) : (
            <Button fontSize="xs" colorScheme="gray" onClick={onClose}>
              Close Window
            </Button>
          )}
        </ModalFooter>
      </ModalContent>
      <ConfirmDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={async () => {
          await handleDeleteRequirement(); // or handleDeleteClient()
          setShowDeleteDialog(false);
        }}
        title="Confirm Deletion"
        body="Are you sure you want to delete this requirement? This cannot be undone."
        confirmText="Yes, Delete"
        cancelText="Cancel"
      />
    </Modal>
  );
};

export default AddEditRequirementModal;
